import { useState } from "react";
import styles from "./login.module.scss";

import { Input, Form, Button, ActionCall, Modal } from "cdhs-ui-library";
import { BsPatchQuestionFill, BsArrowRightShort, BsExclamationTriangleFill } from "react-icons/bs";

import { createUserSchema, userInternalSchema } from "../../services/loginValidation";

import { SubmitHandler } from "react-hook-form";
import { User } from "../../types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { auth, clearError } from "../../store/user/userSlice";
import { useNavigate } from "react-router";
import { ROUTES } from '../../config/routes';
import { useTranslations } from "../../contexts/LanguageContext";
import { isAxiosError } from "axios";
import ContactUsButton from "../../components/ButtonContactUs/ButtonContactUs";

function Login() {
  const dispatch = useAppDispatch();
  let navigate = useNavigate();
  const t = useTranslations();
  const userSchema = createUserSchema(t)
  const stateError = useAppSelector((state) => state.user.error)
  const urlParams = new URLSearchParams(window.location.search);


  const clientID = urlParams.get('cId') ?? '';
  const sType = urlParams.get('sType') ?? '';

  const [internalLogin] = useState(!!clientID);
  const [localError, setLocalError] = useState('');
  const [toggleInput, setToggleInput] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openLoginHelp, setOpenLoginHelp] = useState<boolean>(false);


  // LastName = "TESTYOUTH17";
  // FirstName = "NYTD17";
  // DateOfBirth = "01/01/2008";
  // ClientId = 9999999911;
  // SurveyType = 17;
  // Language = "EN";
  // LoginAttempt = true;


  const handleToggleChange = () => {
    setToggleInput(!toggleInput);
    setLocalError('');
  };

  const openPrivacyPolicy = () => {
    setOpenModal(prevState => !prevState)
  }

  const submitForm: SubmitHandler<User> = async (data) => {
    dispatch(clearError());
    if (!toggleInput) {
      setLocalError('Please read and agree to the Privacy Policy to continue.');
      console.log("Privacy policy must be accepted before logging in.");
      return false;
    }
    setLocalError('');
    try {
      const response = await dispatch(auth({ ...data, LoginAttempt: true })).unwrap();
      if (response.sessionData.SurveyStatus == "C") {
        navigate(ROUTES.USER_PROFILE);
      } else if (response.sessionData.SurveyStatus == "P") {
        navigate(ROUTES.SURVEY_START);
      } else {
        navigate(ROUTES.HOME)
      }
      return true;
    } catch (error) {
      console.log("ERROR IN CATCH, ", error, data);
      // Here we'll check if the error has a message to display
      let message = 'Login failed. Please provide correct input data.';
      if (isAxiosError(error) && error.response) {
        if (stateError != null) {
          return false
        }
        // The API's response body contains the message
        const apilocalError = error.response.data;
        if (apilocalError) {
          message = apilocalError;
          console.log(apilocalError)
          setLocalError(message)
          return false
        }
      }

      // Set the state to display the error message on the UI
      setLocalError(message);
      return false;
    }
  };
  const displayedError = stateError || localError;
  return (
    <div className={styles.overlay}>
      <div className={styles.mainContainer}>
        <div className={styles.header}>
          <h1>{t('loginPage_welcomeTitle', 'Welcome to the NYTD survey.')}</h1>
          <h2>{t('loginPage_subTitle', 'Enter your info to log in the survey.')}</h2>
        </div>
        {displayedError &&
          <div className={styles.error} id="privacy-policy-error" role="alert">
            <p className={styles.errorText}>
              <span>
                <BsExclamationTriangleFill />
              </span>{" "}
              {displayedError}
            </p>
          </div>}

        <Form onSubmit={submitForm} validationSchema={zodResolver(internalLogin ? userInternalSchema : userSchema)}>
          <div className={styles.formContent}>
            {/* Conditional rendering based on the internalLogin flag */}
            {internalLogin ? (
              <>
                {/* Display only the ClientId and SurveyType fields for internal logins */}
                <Input
                  name="ClientId"
                  label={t("loginPage_clientID", "ClientID*")}
                  type="text"
                  // Pass the clientID variable to pre-populate the field
                  value={clientID}
                />
                {/* Display the Survey Type */}
                <Input
                  name="SurveyType"
                  label={t("loginPage_surveyType", "SurveyType*")}
                  type="text"
                  // Pass the clientID variable to pre-populate the field
                  value={sType}
                />
              </>
            ) : (
              <>
                {/* Display the standard fields for external logins */}
                <Input
                  name="FirstName"
                  label={t('loginPage_firstNameLabel', 'First Name*')}
                  type="text"
                />
                <Input
                  name="MiddleName"
                  label={t('loginPage_middleNameLabel', 'Middle Name')}
                  type="text"
                />
                <Input
                  name="LastName"
                  label={t('loginPage_lastNameLabel', 'Last Name*')}
                  type="text"
                />
                <Input
                  name="DateOfBirth"
                  label={t('loginPage_dobLabel', 'Date of Birth* (MM/DD/YYYY)')}
                  type="date"
                />
                <Input
                  name="Email"
                  label={t('loginPage_emailLabel', 'Email (optional)')}
                  type="email"
                  message={t('loginPage_emailMessage', '*This field is optional')}
                />
                {/* cataq: per bug 406777 phone number is not required. */}
                {/* <Input
                  name="PhoneNumber"
                  label={t('loginPage_phoneLabel', 'Phone Number (optional)')}
                  type="text"
                  message={t('loginPage_phoneMessage', '*This field is optional')}
                /> */}
              </>
            )}

            <p><strong>{t('loginPage_privacyPolicyTitle', 'Privacy policy*')}</strong></p>
            <div className={styles.terms}>
              <input
                type="checkbox"
                checked={toggleInput}
                id="Terms"
                onChange={() => handleToggleChange()}
                aria-describedby={localError ? 'privacy-policy-error' : undefined}
              />
              <label htmlFor="Terms">
                {t('loginPage_privacyPolicyCheckboxLabel', 'I agree with the ')}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    openPrivacyPolicy();
                  }}
                  role="button"
                  aria-haspopup="dialog"
                >
                  {t('loginPage_privacyPolicy', 'Privacy Policy')}
                </a>
              </label>
            </div>
            <Button
              type="submit"
              disabled={toggleInput === false}
              caption={t('loginPage_loginButton', 'Log in')}
              iconPosition="Right"
              image={<BsArrowRightShort />}
              variant="NYTD"
              fullWidth={true}
            />
          </div>
        </Form>
        <div className={styles.helpButton}>
          <ActionCall
            label={t('loginPage_needHelpLink', 'Need login help?')}
            variant="NYTD"
            icon={<BsPatchQuestionFill />}
            onClick={() => setOpenLoginHelp(prevState => !prevState)}
          />
        </div>
        <Modal
          open={openModal}
          icon={"Document"}
          onClose={() => setOpenModal(prevState => !prevState)}
          aria-labelledby="privacy-policy-title"
          aria-describedby="privacy-policy-content"
        >
          <h2 id="privacy-policy-title">
            {t('loginPage_privacyPolicy', 'Privacy Notice')}
          </h2>
          <div id="privacy-policy-content" className={styles.privacyPolicyContent}>
            <div>
              <p>{t("loginPage_modalPrivacyPolicyContent_chafeecontent", "The John H. Chafee Foster Care Program for Successful Transition to Adulthood requires the Colorado Department of Human Services (CDHS) to collect and report to the federal Administration for Children and Families (ACF) data on the characteristics of youth receiving independent living services and information regarding their outcomes. ACF uses the information collected in this survey to track independent living services and assess the collective outcomes of youth.")}
              </p>
              <p><strong>OMB Control No:</strong> 0970-0340</p>
            </div>
          <ActionCall label={t("loginPage_modalButton","Close")} onClick={() => setOpenModal(prevState => !prevState)}/>
          </div>

        </Modal>
        <Modal
          open={openLoginHelp}
          onClose={() => setOpenLoginHelp(prevState => !prevState)}
        >
          <h3>{t('loginPage_needHelpLink', 'Need login help?')}</h3>
          <p>{t("loginPage_needLoginHelpModal_content", "Please enter the name and date of birth that your caseworker has on file. If further assistance is needed contact us and we'll be happy to help.")}</p>
          <ContactUsButton onClick={() => {
              navigate(ROUTES.CONTACT)
            }} 
            option="info"
            />
          <div className={styles.helpButton}>
            <ActionCall label={t("loginPage_needLoginHelpModal_closingButton", "Close")} onClick={() => setOpenLoginHelp(prevState => !prevState)} /></div>

        </Modal>
      </div>
    </div>
  );
}

export default Login;
